import { useRef } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import useApi from '../../hooks/useApi'
import { queueStep, readDone, writeDone, queueParams } from './workQueue'

/*
 * The work queue a page is part of (workQueue.js): where the open record
 * stands, and `advance()` — called once it is saved — which opens the next
 * one, or the done page when none is left.
 *
 * A run starts when a record is opened from anywhere but the queue itself
 * (the list, a link): its "done" memory is cleared. Records opened by
 * `advance()` carry `fromQueue` in the navigation state and continue the run.
 *
 * The queue's list is fetched under ['list', entity, …] so whatever refreshes
 * the entity's lists (a save, an SSE broadcast) refreshes it too.
 */
const useWorkQueue = (queue) => {
    const { getMe } = useApi()
    const navigate = useNavigate()
    const location = useLocation()
    const queryClient = useQueryClient()
    const { id } = useParams()

    const started = useRef(false)
    if (queue && !started.current) {
        started.current = true
        if (!location.state?.fromQueue) writeDone(queue, [])
    }

    const queryKey = ['list', queue?.entity, 'workQueue', queue?.filter]
    const fetchIds = async () => {
        const response = await getMe(`${queue.entity}/?${queueParams(queue)}`)
        return (response?.data || []).map(r => r.id)
    }
    const { data: ids } = useQuery({ queryKey, queryFn: fetchIds, enabled: !!queue })

    const step = queue ? queueStep({ ids: ids || [], done: readDone(queue), current: id }) : null

    const advance = async () => {
        const done = [...new Set([...readDone(queue), id])]
        writeDone(queue, done)
        // Fresh: the list as it is now, not as it was when the record opened.
        const fresh = await queryClient.fetchQuery({ queryKey, queryFn: fetchIds, staleTime: 0 })
        const { next } = queueStep({ ids: fresh, done, current: id })
        if (next) navigate(queue.path.replace(':id', next), { state: { fromQueue: true } })
        else {
            writeDone(queue, [])
            navigate(queue.donePath)
        }
    }

    return { step: ids ? step : null, advance }
}

export default useWorkQueue
